import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowsClockwise, LinkSimple, ChartLine, Broadcast, Warning, CheckCircle } from "@phosphor-icons/react";

const BRANDS = [
  { id: "alumnipulse", label: "AlumniPulse (global)" },
  { id: "indiaalumni", label: "IndiaAlumni" },
];

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3" data-testid={`splink-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-2xl font-medium tabular-nums">{value ?? "—"}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function BrandCard({ brand, label, health, analytics, onRegister, busy }) {
  const configured = health?.configured;
  const warn = health?.warning;
  return (
    <Card className="border-border/60 p-5" data-testid={`splink-brand-${brand}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <span className="font-medium">{label}</span>
          {configured ? (
            <Badge variant="secondary" className="gap-1 rounded-full text-[11px] font-normal">
              <CheckCircle size={12} weight="fill" className="text-emerald-600" /> Connected
            </Badge>
          ) : (
            <Badge variant="outline" className="rounded-full text-[11px] font-normal text-amber-700">Native fallback</Badge>
          )}
          {health?.environment && (
            <Badge variant="outline" className="rounded-full text-[11px] font-normal">{health.environment}</Badge>
          )}
        </div>
        <Button size="sm" variant="outline" className="shrink-0 rounded-full gap-1.5" disabled={!configured || busy} onClick={() => onRegister(brand)} data-testid={`splink-register-webhook-${brand}`}>
          <Broadcast size={14} /> Register scan webhook
        </Button>
      </div>

      {!configured && (
        <p className="mt-2 text-xs text-muted-foreground">{health?.detail || "No production tenant key set. Native short links serve meanwhile; they auto-upgrade to splink.pro links once a valid key is configured at deploy."}</p>
      )}
      {warn && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700"><Warning size={13} /> {warn}</p>
      )}
      {health?.error && (
        <p className="mt-2 text-xs text-destructive">SpLink error {health.error.status}: {health.error.detail}</p>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Total clicks" value={analytics?.total_clicks} />
        <Stat label="Links" value={analytics?.links_count} />
        <Stat label="Top country" value={analytics?.by_country?.[0]?.key} />
      </div>
    </Card>
  );
}

export default function SplinkTab() {
  const [health, setHealth] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [scans, setScans] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setHealth(null); setAnalytics(null); setScans(null);
    try {
      const [h, a, s] = await Promise.all([
        api.get("/admin/splink/health"),
        api.get("/admin/splink/analytics"),
        api.get("/admin/splink/scans?limit=25"),
      ]);
      setHealth(h.data || {}); setAnalytics(a.data || {}); setScans(s.data?.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load SpLink data");
      setHealth(false);
    }
  };
  useEffect(() => { load(); }, []);

  const registerWebhook = async (brand) => {
    setBusy(true);
    try {
      await api.post("/admin/splink/webhooks", { brand });
      toast.success("Scan webhook registered — live clicks will stream in.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not register webhook");
    } finally { setBusy(false); }
  };

  if (health === null) return <p className="text-muted-foreground">Loading SpLink analytics…</p>;
  if (health === false) return <p className="text-muted-foreground">Could not load SpLink data.</p>;

  return (
    <div className="space-y-5" data-testid="admin-splink-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground"><LinkSimple size={15} /> Multi-tenant short links, QR & real-time engagement analytics via SpLink.pro.</p>
        <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={load} data-testid="splink-refresh"><ArrowsClockwise size={14} /> Refresh</Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {BRANDS.map((b) => (
          <BrandCard key={b.id} brand={b.id} label={b.label} health={health?.[b.id]} analytics={analytics?.[b.id]} onRegister={registerWebhook} busy={busy} />
        ))}
      </div>

      <Card className="border-border/60 p-5" data-testid="splink-live-ticker">
        <div className="flex items-center gap-2">
          <ChartLine size={16} />
          <span className="font-medium">Live scan ticker</span>
          <Badge variant="secondary" className="rounded-full text-[11px] font-normal">{scans?.length || 0} recent</Badge>
        </div>
        {(!scans || !scans.length) ? (
          <p className="mt-2 text-xs text-muted-foreground">No scans yet. Once tenant keys and the scan webhook are set, every short-link click streams here in real time (donation ticker / chapter dashboard).</p>
        ) : (
          <div className="mt-3 space-y-2">
            {scans.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border/40 px-3 py-2 text-sm" data-testid={`splink-scan-${s.id}`}>
                <Badge variant="outline" className="rounded-full text-[10px] font-normal">{s.brand}</Badge>
                <span className="font-mono text-xs">{s.slug}</span>
                <span className="text-xs text-muted-foreground">{[s.city, s.country].filter(Boolean).join(", ")}</span>
                <span className="text-xs text-muted-foreground">· {s.device}{s.referer ? ` · ${s.referer}` : ""}</span>
                <span className="ml-auto text-xs text-muted-foreground">{s.received_at?.slice(0, 19).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
